import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type { BotThread, MyBot, MyBotsResponse } from '../../lib/types.js';
import { listBotThreads, openBotThread } from './agentClient.js';

export interface MyBotEntry extends MyBot {
  /** Their thread with this bot, or null until they first open it. */
  threadId: string | null;
  /** Messages it sent since they last read its thread. */
  unread: number;
  latestHeadline: string | null;
}

/** How often the bot threads are re-checked for new messages while the app is open. */
export const BOT_CHECK_MS = 60_000;

/**
 * The caller's bots, pinned at the top of the chat sidebar. A bot thread is an ordinary
 * thread the server tags with its bot; it's left out of the normal list, so the chat page
 * learns its id here (`threadIds`) to load it like any other conversation.
 */
export const useMyBotsLogic = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const botsQuery = useQuery({
    queryKey: queryKeys.myBots(),
    queryFn: async (): Promise<MyBot[]> => (await apiRequest<MyBotsResponse>('/api/me/bots')).bots,
    enabled,
  });
  const threadsQuery = useQuery({
    queryKey: queryKeys.agentBotThreads(),
    queryFn: async (): Promise<BotThread[]> => (await listBotThreads()).threads,
    enabled,
    // A bot writes while you're elsewhere: re-check on return and once a minute while open.
    refetchInterval: BOT_CHECK_MS,
  });

  const threads = threadsQuery.data ?? [];
  const bots: MyBotEntry[] = (botsQuery.data ?? []).map((bot) => {
    const thread = threads.find((t) => t.botId === bot.id);
    return {
      ...bot,
      threadId: thread?.conversationId ?? null,
      unread: thread?.unread ?? 0,
      latestHeadline: thread?.latestHeadline ?? null,
    };
  });

  /** Go to the bot's thread, creating it the first time. */
  const openBot = async (bot: MyBotEntry): Promise<void> => {
    if (bot.threadId) {
      navigate(`/chat/${bot.threadId}`);
      return;
    }
    const { conversationId } = await openBotThread(bot.id);
    await queryClient.invalidateQueries({ queryKey: queryKeys.agentBotThreads() });
    navigate(`/chat/${conversationId}`);
  };

  // Off means off: the thread stays readable, nothing new arrives, and replies wait.
  const turnedOff = useMutation<MyBotsResponse, Error, { botId: string; turnedOff: boolean }>({
    mutationFn: ({ botId, turnedOff: off }) =>
      apiRequest<MyBotsResponse>(`/api/me/bots/${encodeURIComponent(botId)}/turned-off`, {
        method: 'PUT',
        body: { turnedOff: off },
      }),
    onSuccess: (fresh) => queryClient.setQueryData(queryKeys.myBots(), fresh.bots),
  });

  return {
    bots,
    /** Turn one of my bots off, or back on. */
    setTurnedOff: (botId: string, off: boolean) => turnedOff.mutateAsync({ botId, turnedOff: off }),
    isSwitching: turnedOff.isPending,
    /** Every bot thread id, so the chat page treats them as server threads. */
    threadIds: threads.map((t) => t.conversationId),
    /** The bot whose thread is open, if any. */
    botForThread: (threadId: string): MyBotEntry | null =>
      bots.find((b) => b.threadId !== null && b.threadId === threadId) ?? null,
    openBot,
  };
};
